import { useBuildCheck } from '@/hooks/use-build-check';
import React, { useEffect, useState, useRef } from 'react';
import DebugButton from '@/components/DebugButton';
import LocationSearch from '@/components/LocationSearch';
import CustomWeatherConditionPicker from '@/components/CustomWeatherConditionPicker';
import WeatherImageDisplay from '@/components/WeatherImageDisplay';
import CustomQuoteInput from '@/components/CustomQuoteInput';
import DatePicker from '@/components/DatePicker';
import { WeatherCondition, LocationOption, WeatherData, WeatherQuote } from '@/types/weather';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wand2 } from 'lucide-react';
import { getRandomTemperature, getRandomHumidity, getRandomWindSpeed } from '@/components/weather/WeatherUtils';

const Index = () => {
  const { checkBuildEnvironment } = useBuildCheck();
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | undefined>();
  const [selectedCondition, setSelectedCondition] = useState<WeatherCondition | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [customQuote, setCustomQuote] = useState<WeatherQuote | null>(null);
  const [forecastDuration, setForecastDuration] = useState<number>(1);
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [showCustomQuoteInput, setShowCustomQuoteInput] = useState<boolean>(false);
  
  const customQuoteRef = useRef<HTMLDivElement>(null);
  
  const scrollToCustomQuote = () => {
    setShowCustomQuoteInput(true);
    if (customQuoteRef.current) {
      customQuoteRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const generateWeatherData = () => {
    if (!selectedLocation || !selectedCondition) return;
    
    const newWeatherData: WeatherData = {
      location: selectedLocation.name,
      date: selectedDate,
      condition: selectedCondition,
      temperature: getRandomTemperature(selectedCondition),
      humidity: getRandomHumidity(selectedCondition),
      windSpeed: getRandomWindSpeed(selectedCondition),
      customQuote: customQuote || undefined,
      forecastDuration: forecastDuration
    };
    
    console.log("Generating weather data:", newWeatherData);
    setWeatherData(newWeatherData);
  };
  
  useEffect(() => {
    generateWeatherData();
  }, [selectedLocation, selectedCondition, selectedDate, customQuote, forecastDuration]);
  
  useEffect(() => {
    console.log('Build check:', checkBuildEnvironment());
    console.log('Application loaded successfully');
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800">
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center mb-6">
          <img 
            src="/lovable-uploads/763e44e8-ef8f-4c41-8496-a43ea9e27d72.png" 
            alt="MeteoWish Logo" 
            className="h-12 mb-2"
          />
          <p className="text-center mb-8 text-gray-600 dark:text-gray-300">
            Customize your weather forecast
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Card>
              <CardContent className="p-5 space-y-4">
                <h2 className="text-xl font-semibold">Location</h2>
                <LocationSearch 
                  onSelectLocation={setSelectedLocation} 
                  selectedLocation={selectedLocation}
                />
                
                <h2 className="text-xl font-semibold mt-6">Date</h2>
                <DatePicker
                  date={selectedDate}
                  onSelect={setSelectedDate}
                  onDurationSelect={setForecastDuration}
                />
                
                <h2 className="text-xl font-semibold mt-6">Weather Conditions</h2>
                <CustomWeatherConditionPicker 
                  selectedCondition={selectedCondition}
                  onSelectCondition={setSelectedCondition}
                  scrollToCustomQuote={scrollToCustomQuote}
                />
                
                {showCustomQuoteInput && (
                  <div ref={customQuoteRef} className="mt-4 pt-4 border-t">
                    <h2 className="text-xl font-semibold">Custom Quote</h2>
                    <CustomQuoteInput 
                      onCustomQuoteChange={setCustomQuote}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <div>
            {weatherData ? (
              <WeatherImageDisplay weatherData={weatherData} />
            ) : (
              <Card className="h-[50vh] flex items-center justify-center">
                <CardContent className="text-center p-6">
                  <h3 className="text-xl font-medium mb-2">Your forecast will appear here</h3>
                  <p className="text-sm text-gray-500">
                    Select a location, date and weather conditions.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      
      <DebugButton />
    </div>
  );
};

export default Index;
